import { getCartErrorMessage } from '@theme/cart-feedback';

class SCardVariantDialog extends HTMLElement {
  #dialog;
  #form;
  #ac;
  #pending;
  #announceFrame;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#form = this.querySelector('[data-variant-form]');
    if (!this.#dialog || !this.#form) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#form.addEventListener('change', (event) => {
      if (!event.target.matches?.('input[name="card_variant_choice"]')) return;
      const choice = event.target;
      this.#form.querySelector('[data-selected-variant]').value = choice.value;
      this.#form.querySelector('[data-selected-quantity]').value = choice.dataset.quantityMin || '1';
      this.#form.querySelector('[data-variant-add]').disabled = false;
      this.#form.querySelector('[data-variant-buy]').disabled = false;
      this.#showError('');
      const announcer = this.#form.querySelector('[data-selected-announcement]');
      cancelAnimationFrame(this.#announceFrame);
      announcer.textContent = '';
      this.#announceFrame = requestAnimationFrame(() => { announcer.textContent = choice.dataset.variantName || ''; });
    }, opts);

    this.#dialog.addEventListener('command', (event) => {
      if (event.command !== '--buy-selected') return;
      const variant = this.#form.querySelector('[data-selected-variant]').value;
      const quantity = this.#form.querySelector('[data-selected-quantity]').value;
      if (!/^\d+$/.test(variant) || !/^\d+$/.test(quantity)) return;
      const cartBase = this.dataset.cartBase?.replace(/\/$/, '');
      if (!cartBase) return;
      window.location.assign(`${cartBase}/${variant}:${quantity}`);
    }, opts);

    this.#form.addEventListener('submit', (event) => {
      if (!window.Shopify?.actions?.updateCart) return; // Native Shopify form remains the fallback.
      event.preventDefault();
      const variant = this.#form.querySelector('[data-selected-variant]').value;
      const quantity = Number(this.#form.querySelector('[data-selected-quantity]').value);
      if (!/^\d+$/.test(variant) || !Number.isSafeInteger(quantity) || quantity < 1) return;
      if (this.#form.getAttribute('aria-busy') === 'true') return;
      this.#add(variant, quantity);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#pending?.abort();
    cancelAnimationFrame(this.#announceFrame);
  }

  async #add(variant, quantity) {
    this.#pending?.abort();
    this.#pending = new AbortController();
    const { signal } = this.#pending;
    const button = this.#form.querySelector('[data-variant-add]');
    button.disabled = true;
    this.#form.setAttribute('aria-busy', 'true');
    this.#showError('');
    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: variant, quantity }] },
        { event: { context: 'product', detail: { source: 's-card-variant-dialog' } }, signal }
      );
      if (signal.aborted) return;
      const error = result?.userErrors?.[0]?.message;
      if (error) {
        this.#showError(error);
      } else {
        this.#dialog.close();
        await window.Shopify.actions.openCart?.();
      }
    } catch (error) {
      if (!signal.aborted) this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#form.removeAttribute('aria-busy');
        button.disabled = false;
        this.#pending = null;
      }
    }
  }

  #showError(message) {
    const error = this.#form.querySelector('[data-variant-error]');
    error.textContent = message;
    error.hidden = !message;
  }
}

if (!customElements.get('s-card-variant-dialog')) {
  customElements.define('s-card-variant-dialog', SCardVariantDialog);
}
